import { Box, Button, Card, Divider, Stack, Typography } from '@mui/joy';
import { addDays, format, isAfter } from 'date-fns';
import React, { FC } from 'react';
import { BsInfoSquare } from 'react-icons/bs';
import { GiEmptyHourglass } from 'react-icons/gi';
import { IoIosWarning } from 'react-icons/io';

import { useFetchOrganizationOfCurrentUser } from '@MP/shared/api/hooks/auth';
import { AgreementType, Patient, useSendPatientInvitation } from '@MP/shared/api/hooks/patient';
import ConfirmDialog from '@MP/shared/components/dialog/ConfirmDialog';
import { useMinddSnackbar } from '@MP/shared/components/MinddSnackbarProvider';
import MemberDeleteIcon from '@MP/shared/icon/MemberDeleteIcon';
import { usePatientStore } from '@MP/shared/providers/PatientProvider';

const TempPatientContents: FC<{ tempUser: Patient }> = ({ tempUser }) => {
  const [openConfirm, setOpenConfirm] = React.useState(false);
  const removeTempUser = usePatientStore((s) => s.removeTempUser);
  const updateTempUserSendTime = usePatientStore((s) => s.updateTempUserSendTime);
  const isExpired = isAfter(new Date(), addDays(new Date(tempUser.createdTime), 7));
  const { mutateAsync: sendPatientInvitation, isPending: isSending } = useSendPatientInvitation();
  const { data: org } = useFetchOrganizationOfCurrentUser();
  const snackbar = useMinddSnackbar();

  const handleReSend = async () => {
    if (!org || !org.id) return;

    await sendPatientInvitation({
      orgId: org.id,
      code: tempUser.code,
      name: tempUser.name,
      phoneNumber: tempUser.phoneNumber,
      birthYear: tempUser.birthYear || 0,
      birthMonth: tempUser.birthMonth || 0,
      birthDay: tempUser.birthDay || 0,
      agreements: [{ type: AgreementType.PatientRegistration }],
    });

    updateTempUserSendTime(tempUser.id, new Date());
    snackbar.add('환자 추가 동의서 재전송이 완료되었습니다.');
  };

  const handleDelete = async (result: boolean) => {
    if (result) {
      removeTempUser(tempUser.id);
      snackbar.add('미동의 환자가 삭제되었습니다.');
    }
    setOpenConfirm(false);
  };

  return (
    <Stack sx={{ overflow: 'auto', height: '100%', position: 'relative' }} rowGap={'8px'}>
      <Stack
        flexGrow={1}
        sx={{
          borderRadius: '8px',
          bgcolor: 'common.white',
          p: '100px',
        }}
        spacing={'8px'}
        alignItems={'center'}>
        <Stack alignItems={'center'}>
          <Divider sx={{ my: '40px', width: 800 }} />
          <Box sx={{ pb: '40px', position: 'relative' }}>
            <GiEmptyHourglass fontSize={80} />
            {isExpired && (
              <Box position={'absolute'} bottom={40} right={-10}>
                <IoIosWarning fontSize={40} color="#ff7400" />
              </Box>
            )}
          </Box>
          {isExpired && (
            <>
              <Typography level="body-md" sx={{ color: '#333' }}>
                아직 서비스 이용에 대한 동의를 완료하지 않은 환자입니다.
              </Typography>
              <Typography level="body-md" color="danger">
                동의 기한이 만료되었습니다. 동의서 재전송 또는 삭제하시기 바랍니다.
              </Typography>
            </>
          )}

          {!isExpired && (
            <>
              <Typography level="body-md" sx={{ color: '#333' }}>
                아직 서비스 이용에 대한 동의를 완료하지 않은 환자입니다.
              </Typography>
              <Typography level="body-md" sx={{ color: '#333' }}>
                동의가 완료될 때까지 잠시만 기다려주세요.
              </Typography>
            </>
          )}
          <Typography level="body-md" sx={{ mt: '24px' }} fontWeight={600}>
            마지막 동의서 전송: {format(new Date(tempUser.createdTime), 'yyyy.MM.dd HH:mm')}
          </Typography>
          <Stack direction={'row'} alignItems={'center'} spacing={'16px'} sx={{ pt: '40px' }}>
            <BsInfoSquare size={'30px'} color="#9a5b13" />
            <Typography level="title-md" color="warning" fontWeight={700}>
              미동의 환자 정보는 등록된 PC에서만 보입니다.
            </Typography>
          </Stack>
          <Divider sx={{ my: '40px', width: 800 }} />
          <Card sx={{ py: '20px' }}>
            <Box>
              <Stack direction={'row'} alignItems={'center'} spacing={'16px'}>
                <Button loading={isSending} variant="outlined" sx={{ width: 120, height: 48 }} onClick={handleReSend}>
                  재전송
                </Button>
                <Typography level="body-md" sx={{ color: '#333' }}>
                  서비스 이용에 대한 동의서를 <b>재전송</b> 합니다.
                </Typography>
              </Stack>
              <Divider sx={{ my: '20px' }} />
              <Stack direction={'row'} alignItems={'center'} spacing={'16px'}>
                <Button
                  variant="outlined"
                  color="danger"
                  sx={{ width: 120, height: 48 }}
                  onClick={() => setOpenConfirm(true)}>
                  삭제
                </Button>
                <Typography level="body-md" sx={{ color: '#333' }}>
                  미동의 상태에서 삭제된 환자라도, 나중에 동의하면 <b>'동의 환자'</b> 목록에 등록됩니다.
                </Typography>
              </Stack>
            </Box>
          </Card>
        </Stack>
      </Stack>

      <ConfirmDialog
        open={openConfirm}
        okColor="danger"
        okText="삭제"
        title="환자 정보 삭제"
        icon={<MemberDeleteIcon width={48} />}
        message={
          <Box textAlign={'center'}>
            <Typography sx={{ color: '#333' }}>{tempUser.name}님 환자 정보를 삭제하시겠습니까?</Typography>
          </Box>
        }
        onResult={handleDelete}
      />
    </Stack>
  );
};

export default TempPatientContents;
